import {
  Download,
  Eye,
  KeyRound,
  ListChecks,
  MoreHorizontal,
  Pencil,
  Power,
  SearchX,
  UserPlus,
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import KycStatusBadge from "@/pages/admin/kyc/components/KycStatusBadge";
import type { KycStatus } from "@/types/kyc";
import { relativeTime } from "@/pages/admin/data";
import RetailerFilterBar from "./components/FilterBar";
import RetailerFormModal from "./components/RetailerFormModal";
import ResetPasswordModal from "./components/ResetPasswordModal";
import DeactivateModal from "./components/DeactivateModal";
import { formatJoined, initials, successBarColor } from "./data";
import type { Retailer, RetailerSummary } from "./types";
import { adminService } from "@/services/apiServices";
import { usePermission } from "@/context/PermissionContext";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];
const DEFAULT_SUMMARY: RetailerSummary = { total: 0, active: 0, inactive: 0, avgSuccessRate: 0, cities: [], states: [] };

export default function AdminRetailers() {
  return (
    <ToastProvider>
      <RetailersInner />
    </ToastProvider>
  );
}

function escapeCsvValue(value: unknown): string {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function RetailersInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const { role, isSuperAdmin, canCreate, canEdit, canDelete } = usePermission();
  const isExecutive = role === "retailer_onboarding_executive" || role === "retailer_onboarding_executive_walletrequesthandler";
  const showOnboardedBy = isSuperAdmin || !isExecutive;
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [list, setList] = useState<Retailer[]>([]);
  const [summary, setSummary] = useState<RetailerSummary>(DEFAULT_SUMMARY);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [city, setCity] = useState("");
  const [sort, setSort] = useState("Newest First");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Retailer | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<Retailer | null>(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<Retailer | null>(null);

  // Debounce free-text search so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any filter change should reset back to page 1.
  useEffect(() => {
    setPage(1);
  }, [search, status, city, sort]);

  const fetchRetailers = useCallback(() => {
    setLoading(true);
    adminService
      .getRetailers({ page, limit: pageSize, search, status, city, sort })
      .then((res) => {
        const data = res?.data;
        setList(data?.retailers ?? []);
        setTotal(data?.pagination?.total ?? 0);
        setSummary(data?.summary ?? DEFAULT_SUMMARY);
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to load retailers");
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, search, status, city, sort, toast]);

  useEffect(() => {
    fetchRetailers();
  }, [fetchRetailers]);

  const chips = useMemo(() => {
    const c: { label: string; onRemove: () => void }[] = [];
    if (searchInput.trim()) c.push({ label: `Search: ${searchInput}`, onRemove: () => setSearchInput("") });
    if (status) c.push({ label: `Status: ${status}`, onRemove: () => setStatus("") });
    if (city) c.push({ label: `City: ${city}`, onRemove: () => setCity("") });
    if (sort !== "Newest First") c.push({ label: `Sort: ${sort}`, onRemove: () => setSort("Newest First") });
    return c;
  }, [searchInput, status, city, sort]);

  function resetFilters() {
    setSearchInput("");
    setStatus("");
    setCity("");
    setSort("Newest First");
  }

  function openAdd() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(r: Retailer) {
    setEditing(r);
    setFormOpen(true);
  }

  async function handleSave(data: Partial<Retailer> & { password?: string }, id?: string) {
    if (!id) {
      const res = await adminService.createRetailer(data);
      toast.success(res?.message || "Retailer added successfully");
    } else {
      const res = await adminService.updateRetailer(id, data);
      toast.success(res?.message || "Retailer updated successfully");
    }
    fetchRetailers();
  }

  async function handleDeactivate(id: string, reason: string, remarks: string) {
    const res = await adminService.toggleRetailerStatus(id, { isActive: false, reason, remarks });
    toast.success(res?.message || "Retailer deactivated");
    fetchRetailers();
  }

  async function handleActivate(r: Retailer) {
    try {
      const res = await adminService.toggleRetailerStatus(r.id, { isActive: true });
      toast.success(res?.message || "Retailer activated");
      fetchRetailers();
    } catch (err: any) {
      toast.error(err?.message || "Failed to activate retailer");
    }
  }

  async function handleResetPassword(id: string, password: string, notify: boolean) {
    const res = await adminService.resetRetailerPassword(id, { password, notify });
    toast.success(res?.message || "Password reset successfully");
  }

  async function handleExport() {
    setExporting(true);
    try {
      const res = await adminService.getRetailers({ page: 1, limit: 10000, search, status, city, sort });
      const rows: Retailer[] = res?.data?.retailers ?? [];
      if (rows.length === 0) {
        toast.error("No retailers to export");
        return;
      }
      const headers = [
        "Retailer ID",
        "Owner Name",
        "Shop Name",
        ...(showOnboardedBy ? ["Onboarded By"] : []),
        "Mobile",
        "Email",
        "City",
        "State",
        "KYC Status",
        "Account Status",
        "Total Mandates",
        "Active Mandates",
        "Success Rate",
        "Joined On",
      ];
      const csvRows = rows.map((r) => [
        r.id,
        r.name,
        r.shopName,
        ...(showOnboardedBy ? [r.createdByName || "Super Admin"] : []),
        r.mobile,
        r.email,
        r.city,
        r.state,
        r.kycStatus,
        r.isActive ? "Active" : "Inactive",
        r.totalMandates,
        r.activeMandates,
        `${r.successRate}%`,
        formatJoined(r.joinedAt),
      ]);
      const csvContent = [headers, ...csvRows].map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `retailers-export-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} retailer${rows.length === 1 ? "" : "s"} successfully`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to export retailers");
    } finally {
      setExporting(false);
    }
  }

  const columns: Column<Retailer>[] = [
    {
      key: "shopName",
      header: "Retailer",
      width: "175px",
      render: (row) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-500">
            {initials(row.name)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-medium text-ink leading-tight">{row.shopName}</p>
            <p className="truncate text-[11px] text-ink-secondary leading-tight mt-0.5">
              {row.name} · {row.userCode || row.id}
            </p>
          </div>
        </div>
      ),
    },
    ...(showOnboardedBy
      ? [
          {
            key: "createdByName" as const,
            header: "Onboarded By",
            width: "135px",
            render: (row: Retailer) => (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                <span className="truncate max-w-[95px]">{row.createdByName || "Super Admin"}</span>
              </span>
            ),
          },
        ]
      : []),
    {
      key: "mobile",
      header: "Contact",
      width: "145px",
      render: (row) => (
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-ink leading-tight">+91 {row.mobile}</p>
          <p className="truncate text-[11px] text-ink-secondary leading-tight mt-0.5">{row.email}</p>
        </div>
      ),
    },
    {
      key: "city",
      header: "Location",
      width: "100px",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-[13px] text-ink leading-tight">{row.city || "—"}</p>
          <p className="truncate text-[11px] text-ink-secondary leading-tight mt-0.5">{row.state || "—"}</p>
        </div>
      ),
    },
    {
      key: "kyc",
      header: "KYC",
      width: "95px",
      render: (row) =>
        row.kycStatus ? (
          <KycStatusBadge status={row.kycStatus as KycStatus} />
        ) : (
          <span className="text-[#94A3B8]">—</span>
        ),
    },
    {
      key: "mandates",
      header: "Mandates",
      width: "80px",
      align: "right",
      render: (row) => (
        <div className="text-right">
          <p className="text-[13px] font-medium text-ink">{row.totalMandates}</p>
          <p className="text-[11px] text-ink-secondary">{row.activeMandates} active</p>
        </div>
      ),
    },
    {
      key: "successRate",
      header: "Success Rate",
      width: "95px",
      render: (row) => {
        const rate = row.successRate;
        const color = successBarColor(rate);
        return (
          <div>
            <p className="text-[13px] font-medium text-ink">{rate}%</p>
            <div className="mt-1 h-1.5 w-14 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full" style={{ width: `${rate}%`, backgroundColor: color }} />
            </div>
          </div>
        );
      },
    },
    {
      key: "joinedAt",
      header: "Joined",
      width: "90px",
      render: (row) => <span className="whitespace-nowrap text-xs text-ink">{formatJoined(row.joinedAt)}</span>,
    },
    {
      key: "lastLoginAt",
      header: "Last Active",
      width: "90px",
      render: (row) => (
        <span className="whitespace-nowrap text-xs text-ink-muted">
          {row.lastLoginAt ? relativeTime(row.lastLoginAt) : "—"}
        </span>
      ),
    },
    {
      key: "isActive",
      header: "Status",
      width: "85px",
      render: (row) =>
        row.isActive ? (
          <span className="inline-flex whitespace-nowrap rounded-full bg-success-bg px-2 py-0.5 text-[11px] font-medium text-emerald-700">
            Active
          </span>
        ) : (
          <span className="inline-flex whitespace-nowrap rounded-full bg-badgeSlate-bg px-2 py-0.5 text-[11px] font-medium text-slate-600">
            Inactive
          </span>
        ),
    },
  ];

  function rowActions(row: Retailer) {
    return (
      <>
        <MenuAction icon={<Eye className="h-4 w-4 text-ink-muted" />} onClick={() => navigate(`/admin/retailers/${row.id}`)}>
          View Details
        </MenuAction>
        {canEdit("retailers") && (
          <>
            <MenuAction icon={<Pencil className="h-4 w-4 text-ink-muted" />} onClick={() => openEdit(row)}>
              Edit Retailer
            </MenuAction>
            <MenuAction
              icon={<KeyRound className="h-4 w-4 text-ink-muted" />}
              onClick={() => {
                setResetTarget(row);
                setResetOpen(true);
              }}
            >
              Reset Password
            </MenuAction>
          </>
        )}
        <MenuAction
          icon={<ListChecks className="h-4 w-4 text-ink-muted" />}
          onClick={() => navigate(`/admin/mandates?retailer=${row.id}`)}
        >
          View Mandates
        </MenuAction>
        {canDelete("retailers") && (
          row.isActive ? (
            <MenuAction
              icon={<Power className="h-4 w-4 text-danger" />}
              danger
              onClick={() => {
                setDeactivateTarget(row);
                setDeactivateOpen(true);
              }}
            >
              Deactivate
            </MenuAction>
          ) : (
            <MenuAction icon={<Power className="h-4 w-4 text-success" />} onClick={() => handleActivate(row)}>
              Activate
            </MenuAction>
          )
        )}
      </>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Retailers</h1>
          <p className="mt-1 text-sm text-ink-secondary">Manage retailer accounts and monitor their activity</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={handleExport}
            loading={exporting}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Export
          </Button>
          {canCreate("retailers") && (
            <Button onClick={openAdd} leftIcon={<UserPlus className="h-4 w-4" />}>
              Add Retailer
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            label="Total Retailers"
            value={`${summary.total}`}
            sub={`across ${summary.cities.length} cities`}
            onClick={() => setStatus("")}
          />
          <SummaryCard
            label="Active"
            value={`${summary.active}`}
            sub={`${summary.total ? Math.round((summary.active / summary.total) * 100) : 0}% of total`}
            onClick={() => setStatus("Active")}
          />
          <SummaryCard
            label="Inactive"
            value={`${summary.inactive}`}
            sub="no mandates can be created"
            onClick={() => setStatus("Inactive")}
          />
          <SummaryCard
            label="Avg. Success Rate"
            value={`${summary.avgSuccessRate}%`}
            sub="platform average"
            onClick={() => navigate("/admin/reports")}
          />
        </div>

        <RetailerFilterBar
          search={searchInput}
          onSearch={setSearchInput}
          status={status}
          onStatus={setStatus}
          city={city}
          onCity={setCity}
          cities={summary.cities}
          sort={sort}
          onSort={setSort}
          onReset={resetFilters}
          chips={chips}
        />

        {loading ? (
          <DataTable columns={columns} data={[]} rowKey={(r) => r.id} loading />
        ) : list.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title="No retailers found"
            description="Try adjusting your filters or search term."
            actionLabel="Reset Filters"
            onAction={resetFilters}
          />
        ) : (
          <DataTable
            columns={columns}
            data={list}
            rowKey={(r) => r.id}
            actions={rowActions}
            onRowClick={(row) => navigate(`/admin/retailers/${row.id}`)}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            maxHeight="520px"
            serverPagination={{
              page,
              pageSize,
              total,
              onPageChange: setPage,
              onPageSizeChange: (n) => {
                setPageSize(n);
                setPage(1);
              },
            }}
          />
        )}
      </div>

      <RetailerFormModal open={formOpen} onClose={() => setFormOpen(false)} retailer={editing} onSave={handleSave} />
      <ResetPasswordModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        retailer={resetTarget}
        onReset={handleResetPassword}
      />
      <DeactivateModal
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        retailer={deactivateTarget}
        onConfirm={handleDeactivate}
      />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  onClick,
}: {
  label: string;
  value: string;
  sub: string;
  onClick?: () => void;
}) {
  const isClickable = !!onClick;
  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`rounded-[10px] border border-line bg-card p-5 shadow-card transition-all ${
        isClickable
          ? "cursor-pointer hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0"
          : ""
      }`}
    >
      <span className="text-[13px] text-ink-secondary">{label}</span>
      <div className="mt-1 text-2xl font-bold text-ink">{value}</div>
      <div className="mt-2 text-xs text-ink-muted">{sub}</div>
    </div>
  );
}

interface MenuActionProps {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
}

function MenuAction({ icon, children, onClick, danger }: MenuActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50 cursor-pointer ${
        danger ? "text-danger" : "text-ink"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}
